import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Typography,
  Link,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AuthApi } from "../../apis";
import { storeSession } from "../../utils/auth";

export default function SignUpForm({ onSwitchToSignIn }) {
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: { name: "", email: "", password: "" },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email address").required("Email is required"),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setError(null);
      try {
        const res = await AuthApi.Register(values);

        if (res?.data?.data?.token) {
          storeSession(res.data.data);

          // A signup always lands in the personal shell. The role it gets is
          // the server's to decide — it used to come from a "type" field in
          // this form, which meant anyone could sign themselves up as an
          // administrator.
          window.location.href = "/user";
          return;
        }

        setError("Signup failed. Please check your details and try again.");
      } catch (err) {
        console.error("Error occurred:", err.response ? err.response.data : err);
        setError(
          err?.response?.data?.message || "An error occurred. Please try again."
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const fieldProps = (name) => ({
    name,
    value: formik.values[name],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    error: Boolean(formik.touched[name] && formik.errors[name]),
    helperText: (formik.touched[name] && formik.errors[name]) || " ",
    fullWidth: true,
    size: "medium",
  });

  return (
    <Box component="form" onSubmit={formik.handleSubmit} noValidate>
      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TextField
        {...fieldProps("name")}
        label="Full name"
        placeholder="Jane Cooper"
        autoComplete="name"
        autoFocus
      />

      <TextField
        {...fieldProps("email")}
        type="email"
        label="Email"
        placeholder="you@company.com"
        autoComplete="email"
      />

      <TextField
        {...fieldProps("password")}
        type={showPassword ? "text" : "password"}
        label="Password"
        placeholder="At least 6 characters"
        autoComplete="new-password"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword((v) => !v)}
                edge="end"
                size="small"
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPassword ? (
                  <VisibilityOffIcon sx={{ fontSize: 20 }} />
                ) : (
                  <VisibilityIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        disabled={formik.isSubmitting}
        sx={{ mt: 1 }}
      >
        {formik.isSubmitting ? "Creating account…" : "Create account"}
      </Button>

      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
        Already have an account?{" "}
        <Link
          component="button"
          type="button"
          onClick={onSwitchToSignIn}
          underline="hover"
          sx={{ fontWeight: 600, verticalAlign: "baseline" }}
        >
          Sign in
        </Link>
      </Typography>
    </Box>
  );
}
